import { isNetworkError } from '@/common/utils/session'

/** Middle AppCode: platform unavailable (often wraps any platform Exception). */
export const MIDDLE_CODE_PLATFORM_UNAVAILABLE = 3000

/** Middle AppCode: no connection to platform. */
export const MIDDLE_CODE_PLATFORM_DISCONNECTED = 4016

const CONNECTIVITY_TEXT_PATTERNS: RegExp[] = [
  /ConnectException/i,
  /SocketTimeoutException/i,
  /UnknownHostException/i,
  /NoRouteToHostException/i,
  /ClosedChannelException/i,
  /Connection refused/i,
  /Connection reset/i,
  /Broken pipe/i,
  /platform is disconnected/i,
  /CONNECTION_ERROR/i,
  /ECONNABORTED/i,
  /ERR_NETWORK/i,
  /Network Error/i,
]

export type MiddleUserFacingErrorInput = {
  code?: number | string | null
  type?: string | null
  message?: string | null
  developerMessage?: string | null
  /** Raw axios/network error, if any. */
  axiosError?: unknown
}

export type MiddleUserFacingErrorKind = 'platformUnavailable' | 'serverError' | 'mapped' | 'unknown'

export type MiddleUserFacingErrorResolution = {
  kind: MiddleUserFacingErrorKind
  /** i18n key under middleErrors.* or full key path when kind is mapped/unknown */
  i18nKey: string
}

function parseCode(code: number | string | null | undefined): number | undefined {
  if (code === undefined || code === null || code === '') return undefined
  const n = typeof code === 'number' ? code : parseInt(String(code), 10)
  return Number.isFinite(n) ? n : undefined
}

function textLooksLikeConnectivityFailure(text: string | null | undefined): boolean {
  if (!text || typeof text !== 'string') return false
  return CONNECTIVITY_TEXT_PATTERNS.some((re) => re.test(text))
}

/**
 * True when the failure is lack of platform connectivity / no response,
 * not an application exception on a live platform.
 */
export function isPlatformConnectivityFailure(input: MiddleUserFacingErrorInput): boolean {
  const code = parseCode(input.code)

  if (code === MIDDLE_CODE_PLATFORM_DISCONNECTED) return true

  if (typeof input.type === 'string' && input.type.toUpperCase() === 'CONNECTION_ERROR') {
    return true
  }

  if (input.axiosError != null) {
    const ax = input.axiosError as { code?: string; message?: string; response?: unknown; request?: unknown }
    if (isNetworkError(ax)) return true
    if (ax.code === 'ECONNABORTED' || ax.code === 'ERR_NETWORK') return true
    if (textLooksLikeConnectivityFailure(ax.message)) return true
  }

  if (textLooksLikeConnectivityFailure(input.developerMessage)) return true
  if (textLooksLikeConnectivityFailure(input.message)) return true

  // Bare 3000 without app/stack details → treat as unavailable
  if (code === MIDDLE_CODE_PLATFORM_UNAVAILABLE) {
    const detail = (input.developerMessage || '').trim()
    if (!detail) return true
    // Non-empty developerMessage that is not connectivity → server-side error wrapped as 3000
    return false
  }

  return false
}

const MAPPED_ERROR_CODE_KEYS: Record<number, string> = {
  1000: 'middleErrors.invalidLoginOrPassword',
  1001: 'middleErrors.platformServiceMode',
  1002: 'middleErrors.accountBlocked',
  2000: 'middleErrors.scriptExecutionError',
  2002: 'middleErrors.websocketResponseTimeout',
  4000: 'middleErrors.sessionExpired',
  4001: 'middleErrors.invalidSessionIdFormat',
  4002: 'middleErrors.invalidAuthTokenFormat',
  4003: 'middleErrors.invalidAuthTokenSignature',
  4004: 'middleErrors.requestCouldNotBeCompleted',
  [MIDDLE_CODE_PLATFORM_DISCONNECTED]: 'middleErrors.platformUnavailable',
}

/**
 * Resolve which user-facing i18n key to show for a middle/platform error.
 * Code 3000 is split: connectivity → platformUnavailable, else → serverError.
 */
export function resolveMiddleUserFacingError(
  input: MiddleUserFacingErrorInput,
): MiddleUserFacingErrorResolution {
  const code = parseCode(input.code)

  if (code === 2002) {
    return { kind: 'mapped', i18nKey: MAPPED_ERROR_CODE_KEYS[2002] }
  }

  if (isPlatformConnectivityFailure(input)) {
    return { kind: 'platformUnavailable', i18nKey: 'middleErrors.platformUnavailable' }
  }

  if (code === MIDDLE_CODE_PLATFORM_UNAVAILABLE) {
    return { kind: 'serverError', i18nKey: 'middleErrors.serverError' }
  }

  if (code !== undefined && MAPPED_ERROR_CODE_KEYS[code]) {
    return { kind: 'mapped', i18nKey: MAPPED_ERROR_CODE_KEYS[code] }
  }

  return { kind: 'unknown', i18nKey: 'middleErrors.unknown' }
}

/** Exported for useErrorHandler / tests — known mapped codes excluding 3000. */
export function getMappedMiddleErrorI18nKey(code: number): string | undefined {
  return MAPPED_ERROR_CODE_KEYS[code]
}
